import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { FaBox, FaServer, FaCheckCircle, FaSpinner, FaEye, FaEyeSlash, FaSync } from 'react-icons/fa';
import { BsGrid, BsList } from 'react-icons/bs';
import { getProjectAssets, ProjectAsset, updateProjectAsset, getProjectAssetStatuses, AssetStatus } from '../../api/assets';
import { createUnit } from '../../api/locations';
import { toast } from 'react-toastify';

interface AddAssetToRackUnitModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  siteId?: string; // For creating rack units
  rackId?: string; // For creating rack units
  unitId: string | null; // Unit ID for API updates (null = create new)
  unitPosition: number;
  totalRackUnits?: number; // Total units in rack (for validation)
  existingUnits?: { position: number; reserved_units: number }[]; // For checking space availability
  onAssetAdded: (assetId: string, unitPosition: number) => void;
  onRackUnitCreated?: () => void; // Callback to refresh rack data
}

export const AddAssetToRackUnitModal: React.FC<AddAssetToRackUnitModalProps> = ({
  isOpen,
  onClose,
  projectId,
  siteId,
  rackId,
  unitId,
  unitPosition,
  totalRackUnits = 45,
  existingUnits = [],
  onAssetAdded,
  onRackUnitCreated
}) => {
  const { keycloak: { token, tokenParsed } } = useKeycloak();
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<ProjectAsset | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState(false);
  const [reservedUnits, setReservedUnits] = useState(1); // How many rack units this asset will occupy
  const [statuses, setStatuses] = useState<AssetStatus[]>([]);
  const [showInstalled, setShowInstalled] = useState(true); // Show/hide installed assets
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid'); // Grid or list view
  const [refreshing, setRefreshing] = useState(false); // Manual refresh state

  // Check if positions are available for multi-unit installation
  const checkPositionsAvailable = (startPosition: number, unitsNeeded: number): boolean => {
    // Check if positions would exceed rack limits
    if (startPosition + unitsNeeded - 1 > totalRackUnits) {
      return false;
    }
    
    // If we're adding to existing unit, just check if we can expand
    if (unitId) {
      // Allow installation to existing unit - space validation is less strict
      return true;
    }
    
    // For new rack units, check if any position is already occupied
    for (let i = 0; i < unitsNeeded; i++) {
      const checkPosition = startPosition + i;
      const isOccupied = existingUnits.some(unit => {
        const unitStart = unit.position;
        const unitEnd = unit.position + (unit.reserved_units || 1) - 1;
        return checkPosition >= unitStart && checkPosition <= unitEnd;
      });
      
      if (isOccupied) {
        return false;
      }
    }
    
    return true;
  };

  // Load assets and statuses when modal opens
  useEffect(() => {
    if (isOpen && token && projectId) {
      loadProjectAssets();
      loadProjectStatuses();
    }
  }, [isOpen, token, projectId]);

  // Auto-refresh when browser window gains focus (like TabAssets)
  useEffect(() => {
    if (!isOpen) return;

    const handleFocus = () => {
      console.log('🔄 Install Asset Modal: Window focused, reloading assets...');
      if (token && projectId) {
        loadProjectAssets(false); // Silent refresh
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && token && projectId) {
        console.log('🔄 Install Asset Modal: Tab visible, reloading assets...');
        loadProjectAssets(false); // Silent refresh
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, token, projectId]);

  const loadProjectAssets = async (isManualRefresh = false) => {
    if (!token) return;
    
    if (isManualRefresh) {
      setRefreshing(true);
      console.log('🔄 Manual refresh triggered');
    } else {
    setLoading(true);
    }
    
    try {
      const response = await getProjectAssets(token, projectId);
      // Show ALL assets without any filtering
      const allAssets = response.results;
      
      const installedCount = allAssets.filter(asset => asset.is_installed).length;
      const availableCount = allAssets.filter(asset => !asset.is_installed).length;
      
      console.log('📦 Assets loaded:', allAssets.length, 'Total:', allAssets.length, 'Installed:', installedCount, 'Available:', availableCount);
      setAssets(allAssets);
      
      if (isManualRefresh) {
        toast.success(`✅ Refreshed! Found ${allAssets.length} assets`);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error('Failed to load assets');
    } finally {
      if (isManualRefresh) {
        setRefreshing(false);
      } else {
      setLoading(false);
      }
    }
  };

  // Manual refresh function
  const handleManualRefresh = () => {
    console.log('🔄 Manual refresh button clicked');
    loadProjectAssets(true);
    loadProjectStatuses();
  };

  const loadProjectStatuses = async () => {
    if (!token) return;
    
    try {
      const response = await getProjectAssetStatuses(token, projectId);
      setStatuses(response.results || []);
    } catch (error) {
      console.error('Error loading asset statuses:', error);
      // Don't show error to user - statuses are optional
    }
  };

  const handleAddAsset = async () => {
    if (!selectedAsset || !token) return;

    // Check if positions are available for multi-unit installation
    if (!checkPositionsAvailable(unitPosition, reservedUnits)) {
      toast.error(`Cannot install asset: positions ${unitPosition} - ${unitPosition + reservedUnits - 1} are not available.`);
      return;
    }

    // Check if statuses are loaded
    if (!statuses || statuses.length === 0) {
      console.error('🚨 CRITICAL ERROR - No statuses loaded! Cannot install asset without status.');
      toast.error('Asset statuses not loaded. Please refresh and try again.');
      return;
    }

    console.log('🚀 STARTING ASSET INSTALLATION');
    console.log('🚀 Selected asset:', selectedAsset.name, selectedAsset.id);
    console.log('🚀 Available statuses count:', statuses.length);

    setAdding(true);
    try {
      let actualUnitId = unitId;

      console.log('🔧 Adding asset to rack unit:', {
        assetId: selectedAsset.id,
        originalUnitId: unitId,
        unitPosition,
        reservedUnits,
        willCreateUnit: !unitId
      });
      
      // If no rack unit exists, create one first (like freeform did)
      if (!unitId && rackId && siteId) {
        console.log('🔧 Creating rack unit for asset installation');
        toast.info('Creating rack unit for your asset...');
        
        const unitData = {
          device_model: selectedAsset.name, // Use asset name as device model
          rack_id: rackId,
          site_id: siteId,
          position: unitPosition,
          reserved_units: reservedUnits,
          power_usage: 0,
        };
        
        const newUnit = await createUnit(token, projectId, siteId, rackId, unitData);
        actualUnitId = newUnit.id;
        console.log('✅ Rack unit created successfully:', actualUnitId);
        
        // Notify parent to refresh data
        if (onRackUnitCreated) {
          onRackUnitCreated();
        }
      }
      // Update asset via API to link it to the rack unit
      const updateData: any = {
        id: selectedAsset.id,
        project_id: projectId,
        item: selectedAsset.item,
        name: selectedAsset.name,
        description: selectedAsset.description,
        quantity: selectedAsset.quantity,
        ordered_at: selectedAsset.ordered_at,
        eta: selectedAsset.eta,
        tracking_number: selectedAsset.tracking_number,
        lead_time: selectedAsset.lead_time,
        esd: selectedAsset.esd,
        isd: selectedAsset.isd,
        current_responsible_person_id: tokenParsed?.sub || selectedAsset.current_responsible_person_id, // Assign current user as responsible
        is_installed: true, // Mark as installed
        notes: selectedAsset.notes,
      };

      console.log('👤 Assigning responsible person:', tokenParsed?.sub ? 'Current user' : 'Existing responsible', tokenParsed?.sub || selectedAsset.current_responsible_person_id);

      // Debug: log all available statuses
      console.log('🔍 STATUS DIAGNOSIS - All available statuses:', statuses.map(s => ({ id: s.id, name: s.name, order: s.status_order })));
      console.log('🔍 STATUS DIAGNOSIS - Statuses array length:', statuses.length);
      console.log('🔍 STATUS DIAGNOSIS - Statuses array:', statuses);

      // Find appropriate status for asset installation
      let selectedStatus = null;
      
      // Priority 1: Look for "Installed" status variations
      const installedStatusNames = ['installed', 'in use', 'active', 'deployed'];
      selectedStatus = statuses.find(status => 
        installedStatusNames.some(name => status.name.toLowerCase().includes(name))
      );
      
      // Priority 2: Look for any status containing "install" 
      if (!selectedStatus) {
        selectedStatus = statuses.find(s => s.name.toLowerCase().includes('install'));
        console.log('🔄 Using install-containing status:', selectedStatus?.name);
      }
      
      // Priority 3: Use the highest order status (most advanced in workflow)
      if (!selectedStatus && statuses.length > 0) {
        selectedStatus = statuses.sort((a, b) => b.status_order - a.status_order)[0];
        console.log('⚠️ Using highest-order status:', selectedStatus.name, 'order:', selectedStatus.status_order);
      }
      
      // Priority 4: Use any available status
      if (!selectedStatus && statuses.length > 0) {
        selectedStatus = statuses[0];
        console.warn('🚨 Using first available status:', selectedStatus.name);
      }
      
      console.log('🔍 STATUS DIAGNOSIS - Selected status:', selectedStatus);
      console.log('🔍 Available statuses:', statuses.map(s => ({ name: s.name, order: s.status_order, id: s.id })));
      
      if (selectedStatus) {
        updateData.current_status_id = selectedStatus.id;
        console.log('✅ STATUS SET - Using status:', selectedStatus.name, 'ID:', selectedStatus.id);
      } else {
        console.error('🚨 CRITICAL - No statuses available! Setting is_installed=true only');
        // Don't set current_status_id if no statuses available
        delete updateData.current_status_id;
      }

      // Only add unit_id if it's a valid UUID (not empty string)
      if (actualUnitId && actualUnitId.trim() !== '') {
        updateData.unit_id = actualUnitId;
      }

      // Debug: log the final payload before API call
      console.log('📤 FINAL PAYLOAD DIAGNOSIS:');
      console.log('📤 Asset ID:', selectedAsset.id);
      console.log('📤 current_status_id in updateData:', updateData.current_status_id);
      console.log('📤 is_installed in updateData:', updateData.is_installed);
      console.log('📤 unit_id in updateData:', updateData.unit_id);
      console.log('📤 Complete updateData object:', JSON.stringify(updateData, null, 2));

      const apiResponse = await updateProjectAsset(token, projectId, selectedAsset.id, updateData);
      
      console.log('📥 API RESPONSE DIAGNOSIS:');
      console.log('📥 Full API response:', apiResponse);
      console.log('📥 API response current_status_id:', apiResponse.current_status_id);
      console.log('📥 API response is_installed:', apiResponse.is_installed);
      console.log('📥 API response unit_id:', apiResponse.unit_id);
      
      // Reload assets list to update installation status
      console.log('🔄 Reloading assets after installation...');
      await loadProjectAssets(false); // Silent refresh - we already show installation success
      
      // Update local UI state
      onAssetAdded(selectedAsset.id, unitPosition);
      const positionText = reservedUnits > 1 
        ? `positions ${unitPosition}-${unitPosition + reservedUnits - 1} (${reservedUnits}U)`
        : `position ${unitPosition} (${reservedUnits}U)`;
      toast.success(`✅ Asset "${selectedAsset.name}" installed in rack ${positionText}`);
      onClose();
    } catch (error) {
      console.error('Error adding asset to rack unit:', error);
      toast.error('Failed to install asset in rack unit');
    } finally {
      setAdding(false);
    }
  };

  // Show assets with search and installed filter
  const filteredAssets = assets.filter(asset => {
    // Apply search filter
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.item.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Apply installed filter (show/hide installed assets)
    const matchesInstalledFilter = showInstalled || !asset.is_installed;
    
    return matchesSearch && matchesInstalledFilter;
  });

  const getAssetStatusColor = (asset: ProjectAsset) => {
    // Use workflow status if available (current_status_id)
    if (asset.status?.name) {
      const statusColorMap: Record<string, string> = {
        'Planning to Purchase': '#888',
        'Ordered': '#3B82F6',
        'Shipped': '#F59E0B', 
        'In Warehouse': '#6366F1',
        'Delivered': '#8B5CF6',
        'Installed': '#059669',  // Workflow installed - darker green
        'In Use': '#059669',
        'Active': '#059669',
        'Deployed': '#059669'
      };
      return statusColorMap[asset.status.name] || '#888';
    }
    
    // Fallback to physical state (is_installed) 
    if (asset.is_installed) {
      return '#10B981'; // Physical installed - brighter green
    }
    
    // Legacy fallback logic
    const now = new Date();
    const eta = new Date(asset.eta);
    
    if (asset.warranty?.is_active_warranty) {
      return '#10B981'; // Brighter green for physical
    } else if (eta <= now) {
      return '#6366F1'; // Purple for in warehouse
    } else {
      return '#F59E0B'; // Orange for shipped
    }
  };

  const getAssetStatusText = (asset: ProjectAsset) => {
    // Use workflow status if available
    if (asset.status?.name) {
      return asset.status.name;
    }
    
    // Fallback to physical state
    if (asset.is_installed) {
      return 'Installed (Physical)';
    }
    
    // Legacy fallback logic
    const now = new Date();
    const eta = new Date(asset.eta);
    
    if (asset.warranty?.is_active_warranty) {
      return 'Installed (Physical)';
    } else if (eta <= now) {
      return 'In Warehouse';
    } else {
      return 'Shipped';
    }
  };

  // List Row Component
  const AssetListRow = ({ asset }: { asset: ProjectAsset }) => {
    const isInstalled = asset.is_installed;
    const isSelectable = !isInstalled;
    const isSelected = selectedAsset?.id === asset.id;

    return (
      <div
        onClick={() => isSelectable && setSelectedAsset(asset)}
        className={`flex items-center p-3 border-b border-[#f5f5f5] transition-all duration-200 ${
          isInstalled 
            ? 'bg-[#fafafa] cursor-not-allowed opacity-60'
            : isSelected
              ? 'bg-[#f5f5f5] cursor-pointer border-l-4 border-l-blue-500'
              : 'hover:bg-[#fafafa] cursor-pointer'
        }`}
      >
        {/* Icon */}
        <div className="flex-shrink-0 mr-3">
          <FaServer className={isInstalled ? "text-[#aaa]" : "text-[#555]"} />
        </div>

        {/* Asset Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className={`font-medium truncate ${isInstalled ? 'text-[#888]' : 'text-[#171717]'}`}>
              {asset.name}
            </h4>
            <div className="flex items-center space-x-2 ml-4">
              {/* Status Badge */}
              <span 
                className={`px-2 py-1 rounded-full text-xs text-white ${isInstalled ? 'opacity-70' : ''}`}
                style={{ backgroundColor: getAssetStatusColor(asset) }}
              >
                {getAssetStatusText(asset)}
              </span>
              
              {/* Installation Status */}
              {isInstalled ? (
                <div className="text-right">
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                    Already Installed
                  </span>
                  {asset.unit && (
                    <div className="text-xs text-green-700 mt-1">
                      Rack: {asset.unit.rack_id?.slice(-8)} | Pos: {asset.unit.position}U
                    </div>
                  )}
                </div>
              ) : isSelected ? (
                <FaCheckCircle className="text-[#888]" />
              ) : null}
            </div>
          </div>
          
          <div className="flex items-center text-sm text-[#888] mt-1 space-x-4">
            <span>Item: <span className="font-mono">{asset.item}</span></span>
            <span>Qty: {asset.quantity}</span>
            {asset.description && (
              <span className="truncate" title={asset.description}>
                {asset.description}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-[#eaeaea]">
          <div className="flex justify-between items-center">
            <div>
              <h2 
                className="text-[20px] font-semibold tracking-[-0.01em] text-[#171717]"
                style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
              >
                {unitId ? 'Add Asset to Rack Unit' : 'Install Asset'}
              </h2>
              <p className="text-[#555] mt-1" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                {unitId 
                  ? `Select an asset to install in existing rack unit at position ${unitPosition}`
                  : `Select an asset to install at position ${unitPosition} (will create rack unit)`
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-[#aaa] hover:text-[#555] text-xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="p-6 border-b border-[#eaeaea]">
          <div className="space-y-4">
            {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search assets by name or item code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
            />
            <FaBox className="absolute left-3 top-3 text-[#aaa]" />
            </div>
            
            {/* Controls Row */}
            <div className="flex items-center justify-between">
              {/* Filter Toggle */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowInstalled(!showInstalled)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    showInstalled 
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-[#f5f5f5] text-[#555] hover:bg-[#eaeaea]'
                  }`}
                  style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
                >
                  {showInstalled ? <FaEye /> : <FaEyeSlash />}
                  <span>{showInstalled ? 'Hide' : 'Show'} Installed</span>
                </button>
                <span className="text-xs text-[#888]">
                  {filteredAssets.filter(a => a.is_installed).length} installed
                </span>
              </div>
              
              {/* Refresh Button */}
              <button
                onClick={handleManualRefresh}
                disabled={refreshing || loading}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  refreshing || loading
                    ? 'bg-[#f5f5f5] text-[#aaa] cursor-not-allowed'
                    : 'bg-[#f5f5f5] text-[#555] hover:bg-[#eaeaea] hover:text-[#171717]'
                }`}
                title="Refresh asset list"
              >
                <FaSync className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              
              {/* View Toggle */}
              <div className="flex items-center bg-[#f5f5f5] rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white text-[#171717] shadow-[0_1px_2px_rgba(0,0,0,0.04)]' 
                      : 'text-[#888] hover:text-[#444]'
                  }`}
                  title="Grid View"
                >
                  <BsGrid />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white text-[#171717] shadow-[0_1px_2px_rgba(0,0,0,0.04)]' 
                      : 'text-[#888] hover:text-[#444]'
                  }`}
                  title="List View"
                >
                  <BsList />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <FaSpinner className="animate-spin text-[#888] text-xl" />
              <span className="ml-2 text-[#555]">Loading assets...</span>
            </div>
          ) : (
            <div className="h-full overflow-y-auto p-6">
              {filteredAssets.length === 0 ? (
                <div className="text-center py-12">
                  <FaBox className="mx-auto text-[#aaa] text-4xl mb-4" />
                  <p className="text-[#888]">
                    {!showInstalled && assets.some(a => a.is_installed) 
                      ? 'No available assets found. Try showing installed assets.'
                      : 'No assets available for installation'
                    }
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredAssets.map(asset => {
                    const isInstalled = asset.is_installed;
                    const isSelectable = !isInstalled;
                    const isSelected = selectedAsset?.id === asset.id;

                    return (
                    <div
                      key={asset.id}
                        onClick={() => isSelectable && setSelectedAsset(asset)}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          isInstalled 
                            ? 'border-[#eaeaea] bg-[#f5f5f5] cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'border-[#888] bg-[#f5f5f5] cursor-pointer'
                              : 'border-[#eaeaea] hover:border-[#eaeaea] hover:bg-[#fafafa] cursor-pointer'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FaServer className={isInstalled ? "text-[#aaa]" : "text-[#555]"} />
                          <h3 
                            className={`font-medium ${isInstalled ? 'text-[#888]' : 'text-[#171717]'}`}
                            style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
                          >
                            {asset.name}
                          </h3>
                        </div>
                        {isInstalled ? (
                          <div className="text-right">
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                              Already Installed
                            </span>
                            {asset.unit && (
                              <div className="text-xs text-green-700 mt-1">
                                <div>Rack: {asset.unit.rack_id?.slice(-8) || 'Unknown'}</div>
                                <div>Position: {asset.unit.position}U</div>
                              </div>
                            )}
                          </div>
                        ) : isSelected ? (
                          <FaCheckCircle className="text-[#888]" />
                        ) : null}
                      </div>
                      
                      <div className="space-y-2 text-sm" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                        <div className="flex justify-between">
                          <span className={isInstalled ? "text-[#aaa]" : "text-[#555]"}>Item Code:</span>
                          <span className={`font-mono ${isInstalled ? 'text-[#aaa]' : 'text-[#171717]'}`}>{asset.item}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isInstalled ? "text-[#aaa]" : "text-[#555]"}>Quantity:</span>
                          <span className={isInstalled ? 'text-[#aaa]' : 'text-[#171717]'}>{asset.quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isInstalled ? "text-[#aaa]" : "text-[#555]"}>Status:</span>
                          <span 
                            className={`px-2 py-1 rounded-full text-xs text-white ${isInstalled ? 'opacity-70' : ''}`}
                            style={{ backgroundColor: getAssetStatusColor(asset) }}
                          >
                            {getAssetStatusText(asset)}
                          </span>
                        </div>
                        {asset.description && (
                          <div className={`mt-2 pt-2 ${isInstalled ? 'border-t border-[#f5f5f5]' : 'border-t border-[#eaeaea]'}`}>
                            <p className={`text-xs ${isInstalled ? 'text-[#aaa]' : 'text-[#555]'}`}>{asset.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                /* List View */
                <div className="divide-y divide-[#f5f5f5]">
                  {filteredAssets.map(asset => (
                    <AssetListRow key={asset.id} asset={asset} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Asset Configuration */}
        {selectedAsset && (
          <div className="px-6 py-4 bg-[#f5f5f5] border-t border-[#eaeaea]">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-[#171717]" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                  Asset Configuration
                </h4>
                <p className="text-sm text-[#171717] mt-1" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                  Selected: <span className="font-semibold">{selectedAsset.name}</span>
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-[#171717]" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                  Rack Units:
                </label>
                <select
                  value={reservedUnits}
                  onChange={(e) => setReservedUnits(parseInt(e.target.value))}
                  className="bg-white border border-[#eaeaea] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#888] font-mono"
                  style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <option key={num} value={num}>{num}U</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-3 text-xs" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}>
              {checkPositionsAvailable(unitPosition, reservedUnits) ? (
                <div className="text-[#171717]">
                  {reservedUnits > 1 ? (
                    <div className="flex items-center space-x-1">
                      <FaServer className="text-[#888]" />
                      <span>✅ Will occupy positions {unitPosition} - {unitPosition + reservedUnits - 1}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <FaServer className="text-[#888]" />
                      <span>✅ Will occupy position {unitPosition} only</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-red-600">
                  <div className="flex items-center space-x-1">
                    <span>❌</span>
                    <span>Not enough space! Positions {unitPosition} - {unitPosition + reservedUnits - 1} are not available.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-[#eaeaea] bg-[#fafafa]">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[#555] hover:text-[#171717] transition-colors"
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddAsset}
              disabled={!selectedAsset || adding || selectedAsset?.is_installed || (selectedAsset && !checkPositionsAvailable(unitPosition, reservedUnits))}
              className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                selectedAsset && !adding && !selectedAsset.is_installed && checkPositionsAvailable(unitPosition, reservedUnits)
                  ? 'bg-[#171717] hover:bg-[#333] text-white'
                  : 'bg-[#ddd] text-[#888] cursor-not-allowed'
              }`}
              style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
            >
              {adding ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Adding...</span>
                </>
              ) : selectedAsset?.is_installed ? (
                <>
                  <FaCheckCircle />
                  <span>Asset Already Installed</span>
                </>
              ) : (
                <>
                  <FaServer />
                  <span>Add Asset to Rack</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 